import { useState } from 'react';
import { useSaveAction } from 'src/pages/actions/useSaveAction';

import { useLinkItems } from '@/hooks/mutations/linked-item/useLinkItems';

interface UseCreateObligationChangeActionArgs {
  /** Parent of the new action. */
  obligationChangeId: string | undefined;
  /** The change's obligation, undefined until the detail query resolves. */
  obligationId: string | undefined;
  /** The detail query's refetch. */
  refetch: () => void;
}

/**
 * The detail page's "create action" flow: `useSaveAction`, plus the two things
 * this page does with the new action before the refetch renders it — link it to
 * the obligation the change belongs to, and close the modal.
 *
 * The modal flag lives here rather than on the page because `onCreated` is what
 * closes it. Split the two and the callback goes back to being an inline page
 * closure, which is only reachable through a render (COI-577).
 */
export const useCreateObligationChangeAction = ({
  obligationChangeId,
  obligationId,
  refetch,
}: UseCreateObligationChangeActionArgs) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { linkItems } = useLinkItems();

  const { saveAction, loading: insertActionLoading } = useSaveAction({
    parentId: obligationChangeId,
    refetch,
    onCreated: async (actionId) => {
      if (obligationId) {
        await linkItems({ Source: obligationId, Targets: [actionId] });
      }
      setIsModalOpen(false);
    },
  });

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    closeModal: () => setIsModalOpen(false),
    createAction: saveAction,
    insertActionLoading,
  };
};
